import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { Table } from 'primeng/table';
import { SmartintegService } from '../../../../services/smartinteg.service';
import { ToastService } from '../../../../services/toast.service';
import { Processo, ExecutarProcessoResult } from '../../../../types/smartinteg';

@Component({
  selector: 'app-processo-list',
  templateUrl: './processo-list.component.html',
  styleUrls: ['./processo-list.component.css'],
  providers: [ConfirmationService]
})
export class ProcessoListComponent implements OnInit {

  @ViewChild('dt') table!: Table;

  processos: Processo[] = [];
  loading: boolean = false;
  executando: boolean = false;

  constructor(
    private smartintegService: SmartintegService,
    private toastService: ToastService,
    private confirmationService: ConfirmationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.carregarProcessos();
  }

  /**
   * Carregar lista de processos
   */
  carregarProcessos(): void {
    this.loading = true;
    this.smartintegService.listarProcessos().subscribe({
      next: (processos) => {
        this.processos = processos;
        this.loading = false;
      },
      error: (error) => {
        this.toastService.erro('Erro', 'Erro ao carregar processos: ' + error.error?.message);
        this.loading = false;
      }
    });
  }

  /**
   * Navegar para criar novo processo
   */
  novoProcesso(): void {
    this.router.navigate(['/smartinteg/processos/novo']);
  }

  /**
   * Navegar para editar processo
   */
  editarProcesso(processo: Processo): void {
    this.router.navigate(['/smartinteg/processos/editar', processo.id]);
  }

  /**
   * Navegar para visualizar recursos do processo
   */
  visualizarRecursos(processo: Processo): void {
    this.router.navigate(['/smartinteg/processos', processo.id, 'fluxo']);
  }

  /**
   * Executar processo
   */
  executarProcesso(processo: Processo): void {
    this.confirmationService.confirm({
      message: `Deseja executar o processo <strong>${processo.nome}</strong>?`,
      header: 'Confirmar Execução',
      icon: 'pi pi-play',
      acceptButtonStyleClass: 'p-button-success',
      acceptLabel: 'Sim, Executar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.executando = true;
        this.smartintegService.executarProcesso(processo.id).subscribe({
          next: (resultado: ExecutarProcessoResult) => {
            if (resultado.sucesso) {
              this.toastService.sucesso('Sucesso', resultado.mensagem || 'Processo executado com sucesso');
            } else {
              this.toastService.alerta('Atenção', resultado.mensagem || 'Processo executado com avisos');
            }
            this.executando = false;
          },
          error: (error) => {
            this.toastService.erro('Erro', 'Erro ao executar processo: ' + error.error?.message);
            this.executando = false;
          }
        });
      }
    });
  }

  /**
   * Alternar status ativo/inativo do processo
   */
  toggleAtivo(processo: Processo): void {
    const novoStatus = !processo.ativo;
    const acao = novoStatus ? 'ativar' : 'desativar';

    this.confirmationService.confirm({
      message: `Deseja ${acao} o processo <strong>${processo.nome}</strong>?`,
      header: 'Confirmar Alteração',
      icon: 'pi pi-question-circle',
      acceptLabel: 'Sim',
      rejectLabel: 'Cancelar',
      accept: () => {
        const processoAtualizado = { ...processo, ativo: novoStatus };
        this.smartintegService.atualizarProcesso(processoAtualizado).subscribe({
          next: () => {
            this.toastService.sucesso('Sucesso', `Processo ${acao === 'ativar' ? 'ativado' : 'desativado'} com sucesso`);
            this.carregarProcessos();
          },
          error: (error) => {
            this.toastService.erro('Erro', `Erro ao ${acao} processo: ` + error.error?.message);
          }
        });
      }
    });
  }

  /**
   * Deletar processo com confirmação
   */
  deletarProcesso(processo: Processo): void {
    this.confirmationService.confirm({
      message: `Tem certeza que deseja deletar o processo <strong>${processo.nome}</strong>?<br><br>Esta ação é irreversível e deletará todos os recursos associados.`,
      header: 'Confirmar Exclusão',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      acceptLabel: 'Sim, Deletar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.loading = true;
        this.smartintegService.deletarProcesso(processo.id).subscribe({
          next: () => {
            this.toastService.sucesso('Sucesso', 'Processo deletado com sucesso');
            this.carregarProcessos();
          },
          error: (error) => {
            this.toastService.erro('Erro', 'Erro ao deletar processo: ' + error.error?.message);
            this.loading = false;
          }
        });
      }
    });
  }

  /**
   * Obter severity para tag de status ativo
   */
  getAtivoSeverity(ativo: boolean): string {
    return ativo ? 'success' : 'danger';
  }

  /**
   * Obter label para status ativo
   */
  getAtivoLabel(ativo: boolean): string {
    return ativo ? 'Ativo' : 'Inativo';
  }

  /**
   * Obter ícone para status ativo
   */
  getAtivoIcon(ativo: boolean): string {
    return ativo ? 'pi-check-circle' : 'pi-times-circle';
  }

  /**
   * Formatar data para exibição
   */
  formatarData(data: Date): string {
    if (!data) return '-';
    return new Date(data).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Limpar filtros da tabela
   */
  limparFiltros(): void {
    this.table.clear();
  }
}
